import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Settings, Image as ImageIcon, Play, Layers as LayersIcon, X } from 'lucide-react';
import config from '../../config';
import { useComponentMetrics, getCosClassName, getCpaClassName, getMetricObjectiveClassName, getMetricLabel, libelleConversions, libelleTauxConversion, infobulleEnteteConversion } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import EnteteAvecBulle from '../EnteteAvecBulle';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';
import { estErreurAcces } from './accesMeta';

const API_URL = config.API_URL;

const ITEMS_PAR_PAGE = 10;

/**
 * Les annonces d'un compte Meta, avec leur visuel.
 *
 * Dernier échelon sous les ensembles de publicités, et le seul tableau du dashboard qui porte
 * une image : c'est au niveau annonce que se juge la création.
 *
 * Le visuel sert à **reconnaître** la ligne, pas à la remplacer — d'où un tableau et non une
 * grille de cartes : les chiffres restent alignés et comparables comme dans les huit autres
 * tables. Le clic agrandit.
 *
 * Les URL viennent du CDN de Meta et portent une signature qui expire : elles se redemandent
 * à chaque chargement. Un bloqueur de publicités les fait échouer sans prévenir, d'où le
 * repli sur un cadre neutre — la ligne et ses chiffres restent lisibles dans tous les cas.
 */
const AdsTable = React.memo(({
  token,
  selectedAccount,
  period,
  customStartDate,
  customEndDate,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccountProp = null,
  selectedCampaignFilter = null,
  selectedPlatformFilter = null,
  labelCampaignIds = null,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'fbads_ads',
  // Le total dédupliqué du compte : voir `celluleTotal`.
  totalCouverture = null,
  totalRepetition = null,
  /**
   * La définition de la métrique Conversions sur ce compte, transmise par `App.js`.
   *
   * Ce tableau interroge sa propre route et n'a donc pas cette information : c'est la timeline
   * du dashboard qui la porte, et elle descend en propriété — comme la couverture dédupliquée.
   * `null` signifie « rien de configuré », et la métrique compte alors les achats.
   */
  conversionConfig = null
}) => {
  const [data, setData] = useState([]);
  const [tronque, setTronque] = useState(false);
  const [plafond, setPlafond] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [agrandie, setAgrandie] = useState(null);

  // Visuels dont le chargement a échoué : mémorisés pour ne pas retenter à chaque rendu et
  // laisser la ligne osciller entre image cassée et cadre neutre.
  const [visuelsEnEchec, setVisuelsEnEchec] = useState(() => new Set());
  /**
   * Le second appel est-il revenu ?
   *
   * Il ne porte pas que les visuels : le statut de diffusion en vient aussi. Sans ce drapeau,
   * la pastille serait rouge — « En veille » — sur toutes les lignes pendant l'attente, puis
   * virerait au vert. Une couleur affirmative sur une information qu'on n'a pas encore est
   * pire qu'une absence de couleur.
   */
  const [visuelsCharges, setVisuelsCharges] = useState(false);

  const componentMetrics = useComponentMetrics(moduleId, selectedAccountProp?.id || selectedAccount || 'global');
  const compteId = selectedAccountProp?.id || selectedAccount || 'global';

  const isComponentMetricActive = useCallback(
    (metricKey) => componentMetrics.metrics.includes(metricKey),
    [componentMetrics.metrics]
  );

  const getLabel = useCallback(
    /*
     * ⚠️ **`conv` et `taux_conv` ne se nomment pas dans une table figée.** Leur intitulé dépend
     * de ce que l'utilisateur a choisi de compter : « Conversions (Ajout au panier) ». Un nom
     * saisi à la main dans la modale l'emporte, et c'est `getMetricLabel` qui le détient.
     */
    (metricKey) => {
      const personnalise = getMetricLabel(metricKey, moduleId, compteId);
      if (metricKey === 'conv') return libelleConversions(conversionConfig, null, 'fbads') || personnalise;
      if (metricKey === 'taux_conv') return libelleTauxConversion(conversionConfig, null, 'fbads') || personnalise;
      return personnalise;
    },
    [moduleId, compteId, conversionConfig]
  );

  // Mêmes clés que les ensembles de publicités : une métrique doit se nommer pareil d'un
  // échelon à l'autre, sinon la comparaison entre les deux tables demande un effort.
  const metricConfig = useMemo(() => ({
    impressions: { sortKey: 'impressions', defaultLabel: 'Impressions' },
    couverture: { sortKey: 'reach', defaultLabel: 'Couverture' },
    repetition: { sortKey: 'frequency', defaultLabel: 'Répétition' },
    ctr: { sortKey: 'ctr', defaultLabel: 'CTR' },
    clics: { sortKey: 'clicks', defaultLabel: 'Clics' },
    cpc: { sortKey: 'cpc', defaultLabel: 'CPC' },
    cout: { sortKey: 'cost', defaultLabel: 'Coût' },
    taux_conv: { sortKey: 'conversionRate', defaultLabel: 'Taux conv.' },
    conv: { sortKey: 'conversions', defaultLabel: 'Conv.' },
    cpa: { sortKey: 'cpa', defaultLabel: 'CPA' },
    valeur_conv: { sortKey: 'conversionsValue', defaultLabel: 'Valeur Conv.' },
    /**
     * Les achats, distincts des conversions.
     *
     * `conv` compte ce que l'utilisateur a configuré ; `transactions` compte les achats, et
     * seulement eux. Sur un compte dont les conversions configurées *sont* les achats, les
     * deux colonnes affichent le même nombre — c'est pour cela que celle-ci est décochée par
     * défaut, et non parce qu'elle serait secondaire.
     */
    transactions: { sortKey: 'transactions', defaultLabel: 'Transactions' },
    cos: { sortKey: 'roas', defaultLabel: 'COS' },
    // ROAS et ROI se déduisent du coût et de la valeur : le tri porte sur ces deux-là,
    // faute d'un champ dédié dans les données de l'annonce.
    roas: { sortKey: 'conversionsValue', defaultLabel: 'ROAS' },
    roi: { sortKey: 'conversionsValue', defaultLabel: 'ROI' },
    videos_vues: { sortKey: 'videoThruplay', defaultLabel: 'Vidéos vues' },
    taux_videos_vues: { sortKey: 'videoThruplay', defaultLabel: '% vidéos vues' },
    commentaires: { sortKey: 'comments', defaultLabel: 'Commentaires' },
    enregistrements: { sortKey: 'saves', defaultLabel: 'Enregistrements' },
    partages: { sortKey: 'shares', defaultLabel: 'Partages' },
    jaime_facebook: { sortKey: 'pageLikes', defaultLabel: "J'aime (Facebook)" },
    interactions: { sortKey: 'interactions', defaultLabel: 'Interactions' },
    cout_par_interaction: { sortKey: 'coutParInteraction', defaultLabel: 'Coût / interaction' },
    reactions: { sortKey: 'reactions', defaultLabel: 'Réactions' },
    // Les deux coûts manquaient à ce registre alors qu'ils étaient sélectionnables ailleurs :
    // le template Engagement les activait, et la colonne ne s'affichait pas. Une métrique
    // proposée à la configuration doit pouvoir se rendre ici, sans quoi le réglage ment.
    cout_par_reaction: { sortKey: 'coutParReaction', defaultLabel: 'Coût / réaction' }
  }), []);

  const formatNombre = useCallback(
    (v) => (formatNumber ? formatNumber(v) : new Intl.NumberFormat('fr-FR').format(v || 0)),
    [formatNumber]
  );
  const formatEuros = useCallback(
    (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0),
    []
  );

  useEffect(() => {
    if (!token || !selectedAccount) return;
    if (period === 'CUSTOM' && (!customStartDate || !customEndDate)) return;

    let annule = false;

    /**
     * Second temps : les visuels, une fois le tableau à l'écran.
     *
     * Les lignes sont enrichies en place plutôt que rechargées — elles gardent leur ordre, leur
     * page et leur tri. Un échec ne dit rien à l'utilisateur : les cadres neutres sont déjà
     * prévus pour une annonce sans visuel, et un bandeau d'erreur ferait douter de chiffres
     * qui, eux, sont bons.
     */
    const chargerVisuels = async () => {
      try {
        const { data: reponse } = await axios.post(
          `${API_URL}/api/facebook-ads/ads/visuels`,
          // Même périmètre que les chiffres : sur un tableau filtré par campagne, demander
          // les créatifs de tout le compte les faisait tomber au-delà du plafond de
          // pagination, et aucun visuel ne revenait.
          {
            accountId: selectedAccount,
            ...(selectedCampaignFilter && { campaignFilter: selectedCampaignFilter })
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (annule) return;
        if (!reponse.success) { setVisuelsCharges(true); return; }

        const visuels = reponse.visuels || {};
        setData(actuelles => actuelles.map(annonce => {
          const v = visuels[String(annonce.id)];
          return v ? { ...annonce, ...v } : annonce;
        }));
        setVisuelsCharges(true);
      } catch (e) {
        // Silence volontaire : voir ci-dessus. Le drapeau passe quand même, sinon les
        // pastilles resteraient grises indéfiniment.
        if (!annule) setVisuelsCharges(true);
      }
    };

    const charger = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: reponse } = await axios.post(
          `${API_URL}/api/facebook-ads/ads`,
          {
            accountId: selectedAccount,
            period,
            customStartDate,
            customEndDate,
            ...(selectedCampaignFilter && { campaignFilter: selectedCampaignFilter }),
            ...(selectedPlatformFilter && { platformFilter: selectedPlatformFilter }),
            ...(labelCampaignIds && labelCampaignIds.length > 0 && { labelCampaignIds }),
            // Les chiffres d'abord. L'appel aux créatifs parcourt toutes les annonces du
            // compte, période ignorée, et retardait l'affichage du tableau de plusieurs
            // secondes pour des vignettes de 64 pixels.
            avecVisuels: false
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (annule) return;
        if (reponse.success) {
          setData(reponse.data || []);
          setTronque(Boolean(reponse.tronque));
          setPlafond(reponse.plafond || 0);
          setVisuelsEnEchec(new Set());
          setVisuelsCharges(false);
          setCurrentPage(1);
          chargerVisuels();
        } else {
          setError(reponse.error || 'Erreur lors du chargement des annonces');
        }
      } catch (e) {
        if (!annule) setError(e.response?.data?.error || 'Erreur lors du chargement des annonces');
      } finally {
        if (!annule) setLoading(false);
      }
    };

    charger();
    // Une période qui change pendant le chargement ne doit pas laisser l'ancienne réponse
    // écraser la nouvelle.
    return () => { annule = true; };
  }, [token, selectedAccount, period, customStartDate, customEndDate,
      selectedCampaignFilter, selectedPlatformFilter, labelCampaignIds]);

  const donneesTriees = useMemo(() => {
    const copie = [...data];
    const { key, direction } = sortConfig;

    /**
     * Valeur de tri d'une annonce.
     *
     * Les deux coûts unitaires ne sont pas dans les données : ils se déduisent. Sans ce calcul,
     * `a[key]` valait `undefined` et la colonne, triable en apparence, ne réordonnait rien.
     *
     * Une annonce sans interaction ni réaction part en fin de tri croissant plutôt qu'en tête :
     * un coût nul n'est pas le meilleur coût, c'est une absence de mesure.
     */
    const valeurDeTri = (annonce) => {
      if (key === 'coutParInteraction') {
        return annonce.interactions > 0 ? annonce.cost / annonce.interactions : Number.MAX_VALUE;
      }
      if (key === 'coutParReaction') {
        return annonce.reactions > 0 ? annonce.cost / annonce.reactions : Number.MAX_VALUE;
      }
      return annonce[key];
    };

    copie.sort((a, b) => {
      const va = valeurDeTri(a);
      const vb = valeurDeTri(b);
      if (typeof va === 'string' || typeof vb === 'string') {
        return direction === 'asc'
          ? String(va || '').localeCompare(String(vb || ''))
          : String(vb || '').localeCompare(String(va || ''));
      }
      return direction === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return copie;
  }, [data, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(donneesTriees.length / ITEMS_PAR_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PAR_PAGE;
  const page = donneesTriees.slice(startIndex, startIndex + ITEMS_PAR_PAGE);

  const handleSort = useCallback((key) => {
    setSortConfig(actuel => ({
      key,
      direction: actuel.key === key && actuel.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(1);
  }, []);

  const signalerEchec = useCallback((id) => {
    setVisuelsEnEchec(actuels => new Set(actuels).add(id));
  }, []);

  /** La vignette, ou le cadre neutre quand il n'y a rien à montrer. */
  const Vignette = ({ annonce }) => {
    const indisponible = !annonce.vignetteUrl || visuelsEnEchec.has(annonce.id);
    const cadre = {
      width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0,
      border: '1px solid #e5e7eb', objectFit: 'cover', display: 'block'
    };

    if (indisponible) {
      return (
        <div
          style={{ ...cadre, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6' }}
          title={annonce.estCarrousel ? 'Carrousel sans visuel unique' : 'Visuel indisponible'}
        >
          {annonce.estCarrousel ? <LayersIcon size={18} color="#9ca3af" /> : <ImageIcon size={18} color="#9ca3af" />}
        </div>
      );
    }

    // Meta ne sert qu'un 64 x 64 pour `thumbnail_url` ; le plein format vient d'ailleurs et
    // n'existe pas toujours — pour une vidéo, son poster demande une permission que la
    // connexion n'a pas. Proposer le clic sans lui n'afficherait qu'une vignette étirée.
    const agrandissable = annonce.visuelPleineTaille && !visuelsEnEchec.has(annonce.id);

    return (
      <div style={{ position: 'relative', width: '48px', height: '48px', flexShrink: 0 }}>
        <img
          src={annonce.vignetteUrl}
          alt=""
          // Le navigateur télécharge au fil du défilement, et hors du fil principal : sur une
          // page de cinquante annonces, toutes les vignettes partaient ensemble, y compris
          // celles qu'on ne verrait jamais.
          loading="lazy"
          decoding="async"
          title={agrandissable ? 'Agrandir' : (annonce.estVideo ? 'Aperçu vidéo indisponible en grand format' : undefined)}
          style={{ ...cadre, cursor: agrandissable ? 'zoom-in' : 'default' }}
          onClick={agrandissable ? () => setAgrandie(annonce) : undefined}
          onError={() => signalerEchec(annonce.id)}
        />
        {annonce.estVideo && (
          <span style={{
            position: 'absolute', bottom: '2px', right: '2px',
            width: '16px', height: '16px', borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Play size={9} fill="white" />
          </span>
        )}
      </div>
    );
  };

  /** Une cellule de métrique, selon la clé demandée par la configuration du module. */
  const cellule = (annonce, metricKey) => {
    const cpa = annonce.conversions > 0 ? annonce.cost / annonce.conversions : 0;
    const cos = annonce.conversionsValue > 0 ? (annonce.cost / annonce.conversionsValue) * 100 : 0;

    switch (metricKey) {
      case 'impressions': return <td key={metricKey} className="numeric">{formatNombre(annonce.impressions)}</td>;
      /* Meta déduplique la couverture au sein d'une annonce : la valeur de la ligne est juste.
         Leur somme ne l'est pas — voir le tiret posé sur la ligne de total. */
      case 'couverture': return <td key={metricKey} className="numeric">{formatNombre(annonce.reach)}</td>;
      case 'repetition': return <td key={metricKey} className="numeric">{(annonce.frequency || 0).toFixed(2)}</td>;
      case 'ctr': return <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('ctr', annonce.ctr, compteId)}`}>{(annonce.ctr || 0).toFixed(2)} %</td>;
      case 'clics': return <td key={metricKey} className="numeric">{formatNombre(annonce.clicks)}</td>;
      case 'cpc': return <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cpc', annonce.cpc, compteId)}`}>{formatEuros(annonce.cpc)}</td>;
      case 'cout': return <td key={metricKey} className="numeric">{formatEuros(annonce.cost)}</td>;
      case 'taux_conv': return <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('taux_conv', annonce.conversionRate, compteId)}`}>{(annonce.conversionRate || 0).toFixed(2)} %</td>;
      case 'conv': return <td key={metricKey} className="numeric">{formatNombre(annonce.conversions)}</td>;
      case 'transactions': return <td key={metricKey} className="numeric">{formatNombre(annonce.transactions || 0)}</td>;
      case 'cpa': return <td key={metricKey} className={`numeric ${getCpaClassName(cpa, compteId)}`}>{formatEuros(cpa)}</td>;
      case 'valeur_conv': return <td key={metricKey} className="numeric">{formatEuros(annonce.conversionsValue)}</td>;
      case 'cos': return <td key={metricKey} className={`numeric ${getCosClassName(cos, compteId)}`}>{cos > 0 ? `${cos.toFixed(1)} %` : '—'}</td>;
      /**
       * ROAS et ROI : deux lectures du même rapport, gardées distinctes.
       *
       * Le ROAS dit ce que rapporte un euro dépensé, en pourcentage — 400 % pour quatre
       * euros de chiffre d'affaires. Le ROI retranche la dépense et rend un multiple —
       * 3 pour le même cas. Un tiret, et non zéro, tant qu'il n'y a pas de dépense : un
       * rendement nul se lirait comme un échec là où il n'y a rien à mesurer.
       */
      case 'roas': {
        const roas = annonce.cost > 0 ? (annonce.conversionsValue / annonce.cost) * 100 : null;
        return <td key={metricKey} className="numeric">{roas === null ? '—' : `${roas.toFixed(0)} %`}</td>;
      }
      case 'roi': {
        const roi = annonce.cost > 0 ? (annonce.conversionsValue - annonce.cost) / annonce.cost : null;
        return <td key={metricKey} className="numeric">{roi === null ? '—' : roi.toFixed(2)}</td>;
      }
      /**
       * Vidéo : les ThruPlay, et leur part des impressions.
       *
       * Un tiret, et non zéro, sur une annonce sans vidéo : « 0 vue » se lirait comme une
       * vidéo que personne n'a regardée, là où il n'y a pas de vidéo du tout. La distinction
       * se fait sur l'absence du champ, que l'API ne renseigne que pour les formats vidéo.
       */
      case 'videos_vues': return (
        <td key={metricKey} className="numeric">
          {annonce.videoThruplay == null ? '—' : formatNombre(annonce.videoThruplay)}
        </td>
      );
      case 'taux_videos_vues': {
        const taux = annonce.impressions > 0 && annonce.videoThruplay != null
          ? (annonce.videoThruplay / annonce.impressions) * 100
          : null;
        return <td key={metricKey} className="numeric">{taux === null ? '—' : `${taux.toFixed(2)} %`}</td>;
      }
      /**
       * Interactions sociales : zéro est ici une information, pas une absence.
       *
       * Contrairement à la vidéo, toute publication peut être commentée ou partagée :
       * « 0 commentaire » dit quelque chose de la créa, là où « 0 vue » sur une annonce
       * sans vidéo ne dirait rien du tout. D'où un chiffre, et non un tiret.
       */
      case 'commentaires': return <td key={metricKey} className="numeric">{formatNombre(annonce.comments)}</td>;
      case 'enregistrements': return <td key={metricKey} className="numeric">{formatNombre(annonce.saves)}</td>;
      case 'partages': return <td key={metricKey} className="numeric">{formatNombre(annonce.shares)}</td>;
      case 'jaime_facebook': return <td key={metricKey} className="numeric">{formatNombre(annonce.pageLikes)}</td>;
      case 'interactions': return <td key={metricKey} className="numeric">{formatNombre(annonce.interactions)}</td>;
      case 'cout_par_interaction': return <td key={metricKey} className="numeric">{annonce.interactions > 0 ? formatEuros(annonce.cost / annonce.interactions) : '—'}</td>;
      case 'reactions': return <td key={metricKey} className="numeric">{formatNombre(annonce.reactions)}</td>;
      // Un tiret et non « 0 € » quand rien n'a été réagi : un coût nul se lirait comme une
      // performance parfaite alors qu'il n'y a simplement rien à mesurer.
      case 'cout_par_reaction': return <td key={metricKey} className="numeric">{annonce.reactions > 0 ? formatEuros(annonce.cost / annonce.reactions) : '—'}</td>;
      default: return <td key={metricKey} className="numeric">—</td>;
    }
  };

  const totaux = useMemo(() => donneesTriees.reduce((acc, a) => ({
    impressions: acc.impressions + (a.impressions || 0),
    clicks: acc.clicks + (a.clicks || 0),
    cost: acc.cost + (a.cost || 0),
    conversions: acc.conversions + (a.conversions || 0),
    conversionsValue: acc.conversionsValue + (a.conversionsValue || 0),
    transactions: acc.transactions + (a.transactions || 0),
    interactions: acc.interactions + (a.interactions || 0),
    reactions: acc.reactions + (a.reactions || 0),
    videoThruplay: acc.videoThruplay + (a.videoThruplay || 0),
    comments: acc.comments + (a.comments || 0),
    saves: acc.saves + (a.saves || 0),
    shares: acc.shares + (a.shares || 0),
    pageLikes: acc.pageLikes + (a.pageLikes || 0)
  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, videoThruplay: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0 }),
  [donneesTriees]);

  const celluleTotal = (metricKey) => {
    const ctr = totaux.impressions > 0 ? (totaux.clicks / totaux.impressions) * 100 : 0;
    const cpc = totaux.clicks > 0 ? totaux.cost / totaux.clicks : 0;
    const tauxConv = totaux.clicks > 0 ? (totaux.conversions / totaux.clicks) * 100 : 0;
    const cpa = totaux.conversions > 0 ? totaux.cost / totaux.conversions : 0;
    const cos = totaux.conversionsValue > 0 ? (totaux.cost / totaux.conversionsValue) * 100 : 0;

    // Des ratios de sommes, jamais des moyennes de ratios : une annonce à 3 impressions
    // pèserait autant qu'une à trente mille.
    const valeurs = {
      impressions: formatNombre(totaux.impressions),
      /**
       * Le total vient du compte, jamais de la somme des annonces.
       *
       * Deux annonces d'un même ensemble touchent largement les mêmes comptes : additionner
       * leurs couvertures compterait plusieurs fois les mêmes personnes, et la répétition est un
       * ratio bâti dessus. Seul Meta sait déduplier, et il le fait pour le compte entier — le
       * dashboard fait descendre cette valeur, et la retient dès qu'un filtre rétrécit le
       * tableau : un tiret vaut mieux qu'un chiffre portant sur un autre périmètre.
       */
      couverture: totalCouverture == null ? '—' : formatNombre(totalCouverture),
      repetition: totalRepetition == null ? '—' : totalRepetition.toFixed(2),
      ctr: `${ctr.toFixed(2)} %`,
      clics: formatNombre(totaux.clicks),
      cpc: formatEuros(cpc),
      cout: formatEuros(totaux.cost),
      taux_conv: `${tauxConv.toFixed(2)} %`,
      conv: formatNombre(totaux.conversions),
      transactions: formatNombre(totaux.transactions),
      cpa: formatEuros(cpa),
      valeur_conv: formatEuros(totaux.conversionsValue),
      cos: cos > 0 ? `${cos.toFixed(1)} %` : '—',
      roas: totaux.cost > 0 ? `${((totaux.conversionsValue / totaux.cost) * 100).toFixed(0)} %` : '—',
      roi: totaux.cost > 0 ? (((totaux.conversionsValue - totaux.cost) / totaux.cost)).toFixed(2) : '—',
      interactions: formatNombre(totaux.interactions),
      // Ratios de sommes, comme les autres totaux de cette ligne.
      cout_par_interaction: totaux.interactions > 0 ? formatEuros(totaux.cost / totaux.interactions) : '—',
      reactions: formatNombre(totaux.reactions),
      cout_par_reaction: totaux.reactions > 0 ? formatEuros(totaux.cost / totaux.reactions) : '—',
      videos_vues: totaux.videoThruplay > 0 ? formatNombre(totaux.videoThruplay) : '—',
      taux_videos_vues: totaux.videoThruplay > 0 && totaux.impressions > 0
        ? `${((totaux.videoThruplay / totaux.impressions) * 100).toFixed(2)} %`
        : '—',
      commentaires: formatNombre(totaux.comments),
      enregistrements: formatNombre(totaux.saves),
      partages: formatNombre(totaux.shares),
      jaime_facebook: formatNombre(totaux.pageLikes)
    };
    return <td key={metricKey} className="numeric"><strong>{valeurs[metricKey] ?? '—'}</strong></td>;
  };

  /**
   * Accès Meta expiré : la section disparaît, le bandeau de tête suffit.
   *
   * Elle affichait sinon le code brut du serveur — « ❌ auth_expired » — sous son titre, alors
   * que l'écran porte déjà, une fois, l'avertissement et le bouton qui le résout. Un bloc vide
   * sous son intitulé se lit comme une panne de la section ; c'est la connexion qui est en
   * cause, et elle est annoncée là où on peut agir.
   */
  if (estErreurAcces(error)) return null;

  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) return null;

  // Rien de significatif : la section ne s'affiche pas du tout, comme ses sœurs.
  const aDesDonnees = data.some(a => a.impressions > 0 || a.clicks > 0 || a.cost > 0);
  if (!loading && !error && !aDesDonnees) return null;

  const colonnesActives = componentMetrics.order.filter(k => isComponentMetricActive(k) && metricConfig[k]);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ImageIcon size={20} /> Annonces
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Annonces"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? 'Masquer la section' : 'Afficher la section'}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <>
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Chargement des annonces…</span>
            </div>
          )}

          {error && (
            <div className="error-container">
              <span>❌ {error}</span>
            </div>
          )}

          {!loading && !error && donneesTriees.length === 0 && (
            <div className="no-data-container">
              <span>Aucune donnée disponible pour cette période</span>
            </div>
          )}

          {!loading && !error && donneesTriees.length > 0 && (
            <>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Visuel</th>
                      {/* Une largeur donnée à la colonne des noms : sans elle, `tableLayout:
                          auto` lui laissait tout l'espace restant, et les chiffres se
                          retrouvaient rejetés à des centaines de pixels du titre qu'ils
                          qualifient. */}
                      <th onClick={() => handleSort('name')} className="sortable libelle-colonne" style={{ width: '34%' }}>
                        Annonce {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {colonnesActives.map(metricKey => {
                        const conf = metricConfig[metricKey];
                        return (
                          <EnteteAvecBulle
                            key={metricKey}
                            onClick={() => handleSort(conf.sortKey)}
                            className="sortable numeric"
                            texte={infobulleEnteteConversion(metricKey, conversionConfig)}
                          >
                            {getLabel(metricKey) || conf.defaultLabel}
                            {sortConfig.key === conf.sortKey && (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
                          </EnteteAvecBulle>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {page.map(annonce => (
                      <tr key={annonce.id}>
                        <td><Vignette annonce={annonce} /></td>
                        {/* `campaign-name` porte déjà la contrainte de largeur et le retour à
                            la ligne, comme dans les tableaux voisins : un titre long se replie
                            plutôt que d'élargir la colonne. */}
                        {/* `libelle-colonne` : ce tableau est le seul où le libellé n'est pas
                            la première colonne — la vignette le précède. Sans cette marque,
                            la règle générique des colonnes de métriques le centrait. */}
                        <td className="campaign-name libelle-colonne">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Même code couleur que les campagnes et les ensembles : seul
                                « ACTIVE » diffuse, tout le reste est en veille. */}
                            <span
                              style={{
                                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                                backgroundColor: !visuelsCharges
                                  ? '#d1d5db'
                                  : (annonce.isDelivering ? '#22c55e' : '#ef4444')
                              }}
                              title={!visuelsCharges
                                ? 'Statut en cours de chargement'
                                : (annonce.isDelivering ? 'Diffusion en cours' : 'En veille')}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 500 }}>{annonce.name}</div>
                              {annonce.campaignName && (
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{annonce.campaignName}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        {colonnesActives.map(metricKey => cellule(annonce, metricKey))}
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td></td>
                      <td className="libelle-colonne"><strong>TOTAL</strong></td>
                      {colonnesActives.map(metricKey => celluleTotal(metricKey))}
                    </tr>
                  </tfoot>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {startIndex + 1}-{Math.min(startIndex + ITEMS_PAR_PAGE, donneesTriees.length)} sur {donneesTriees.length} annonces
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">Page {currentPage} sur {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}

              {/* Une troncature se dit : un tableau muet passe pour exhaustif. */}
              {tronque && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: '8px' }}>
                  Ce compte dépasse {formatNombre(plafond)} annonces : la liste est tronquée et
                  les totaux ci-dessus ne portent que sur les annonces affichées.
                </p>
              )}
            </>
          )}
        </>
      )}

      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId={moduleId}
          accountId={compteId}
          title="Annonces Meta Ads"
          hideApplyToAll={true}
          /*
           * La définition de la métrique Conversions se règle ici, dans l'engrenage de la
           * métrique — comme sur le dashboard Google Analytics. Ces quatre propriétés sont
           * ce dont la modale a besoin pour la charger, l'enregistrer et la rendre.
           *
           * `onAdvancedConfigSave` recharge : les séries et tous les tableaux sont calculés
           * côté serveur avec l'ancienne définition. Sans ce rechargement, l'écran afficherait
           * le nouveau nom au-dessus des anciens chiffres — pire qu'un écran inchangé,
           * puisque rien ne le signalerait.
           */
          token={token}
          propertyId={selectedAccountProp?.id || selectedAccount}
          currentAdvancedConfig={conversionConfig}
          onAdvancedConfigSave={() => window.location.reload()}
        />
      )}

      {/* Agrandissement : portalisé, comme toutes les modales du projet. */}
      {agrandie && ReactDOM.createPortal(
        <div
          onClick={() => setAgrandie(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '24px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '16px',
              width: '100%', maxWidth: '640px', maxHeight: '90vh',
              overflow: 'auto', position: 'relative'
            }}
          >
            <button
              onClick={() => setAgrandie(null)}
              title="Fermer"
              style={{
                position: 'absolute', top: '10px', right: '10px', background: 'white',
                border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
                display: 'flex', padding: '4px'
              }}
            >
              <X size={16} />
            </button>
            {/* `width: 100%` et non `maxWidth` : l'original fait 1080 px de large, il doit
                occuper la modale plutôt que de flotter à sa taille naturelle. */}
            <img
              src={agrandie.visuelUrl}
              alt={agrandie.name}
              style={{
                width: '100%', maxHeight: '78vh', objectFit: 'contain',
                display: 'block', borderRadius: '8px', backgroundColor: '#f3f4f6'
              }}
              onError={() => { signalerEchec(agrandie.id); setAgrandie(null); }}
            />
            <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: 600 }}>{agrandie.name}</div>
            {agrandie.campaignName && (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{agrandie.campaignName}</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export default AdsTable;
